import React, { createContext, useContext, useReducer, useEffect, ReactNode, useState, useCallback } from 'react';
import { Cart, CartItem } from '../types/cart';
import { getCurrentLanguage, LANGUAGE_CHANGE_EVENT, SupportedLanguage } from '../config/languageConfig';
import { getCurrencyFromLanguage } from '../utils/currencyUtils';
import { createCart, getCart, transformCartToRequest, CartResponse } from '../api/cartService';
import { createCartGift } from '../api/cartService';
import { getCurrentServiceUserId } from '../services/OperationTimeService';

// 최소 주문 금액 인터페이스 정의 (서버 응답 구조에 맞게 수정)
interface MinOrderAmounts {
  krw: number;
  usd: number;
  jpy: number;
  cny: number;
  twd: number;
}

interface RestaurantInfo {
  restaurantName: string;
  restaurantNameEn?: string;
  restaurantNameJa?: string;
  restaurantNameZhCn?: string;
  restaurantNameZhTw?: string;
  restaurantNameKo?: string;
}

// 카트 액션 타입 정의
type CartAction = 
  | { type: 'ADD_ITEM'; payload: { restaurantId: string; restaurantInfo: RestaurantInfo; item: CartItem } }
  | { type: 'UPDATE_ITEM_QUANTITY'; payload: { itemId: string; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: { itemId: string } }
  | { type: 'CLEAR_CART' }
  | { type: 'REPLACE_CART'; payload: { restaurantId: string; restaurantInfo: RestaurantInfo; item: CartItem } }
  | { type: 'UPDATE_PRICES'; payload: { language: SupportedLanguage } }
  | { type: 'SET_ORDER_ID'; payload: { orderId: string } };

// 카트 컨텍스트 타입 정의
interface CartContextType {
  cart: Cart | null;
  currentRestaurantId: string | null;
  setCurrentRestaurantId: (id: string | null) => void;
  showCartReplaceConfirm: boolean;
  pendingCartItem: { restaurantId: string; restaurantInfo: RestaurantInfo; item: CartItem } | null;
  canceledItemId: string | null;
  restaurantMinOrderAmount: MinOrderAmounts | null;
  setRestaurantMinOrderAmount: (amounts: MinOrderAmounts | null) => void;
  addToCart: (restaurantId: string, restaurantInfo: RestaurantInfo, item: CartItem) => void;
  updateItemQuantity: (itemId: string, quantity: number) => void;
  removeItem: (itemId: string) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
  getTotalItems: () => number;
  getMinOrderAmount: () => number | null;
  isDeliveryAvailable: (minOrderAmount: number | null) => boolean;
  getAmountToMinOrder: (minOrderAmount: number | null) => number;
  switchMessage: string | null;
  clearSwitchMessage: () => void;
  confirmReplaceCart: () => void;
  cancelReplaceCart: () => void;
  clearCanceledItem: () => void;
  updateCartPricesForLanguage: (language: SupportedLanguage) => void;
  fetchOrderId: (addressId: string, reservationDateTime?: string, originStayId?: string) => Promise<CartResponse | null>;
}

// 카트 데이터 버전 관리
const CART_DATA_VERSION = 2; // 프로모션 필드 추가 버전

// 로컬 스토리지에서 카트 데이터 로드
const loadCartFromStorage = (): Cart | null => {
  try {
    const cartData = localStorage.getItem('cart');
    const version = localStorage.getItem('cartVersion');
    
    // 구버전 데이터는 삭제하고 새로 시작
    if (!version || parseInt(version) < CART_DATA_VERSION) {
      console.log('Upgrading cart data version, clearing old cart data');
      localStorage.removeItem('cart');
      localStorage.setItem('cartVersion', CART_DATA_VERSION.toString());
      return null;
    }
    
    if (!cartData) return null;
    
    const parsedCart = JSON.parse(cartData) as Cart;
    
    // 최소한의 유효성 검사 - 필수 필드가 있는지 확인
    if (!parsedCart.restaurantId || !parsedCart.restaurantName || !Array.isArray(parsedCart.items)) {
      console.error('Invalid cart data in localStorage, missing required fields');
      localStorage.removeItem('cart'); // 잘못된 데이터 삭제
      return null;
    }
    
    // 아무 검사도 하지 않고 그냥 반환 (가게 이름 보존)
    return parsedCart;
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    return null;
  }
};

// 카트 리듀서 함수
const cartReducer = (state: Cart | null, action: CartAction): Cart | null => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { restaurantId, restaurantInfo, item } = action.payload;
      
      // 카트가 비어있는 경우 - 새 카트 생성
      if (!state) {
        // 현재 표시 언어에 맞는 레스토랑 이름을 기본으로 사용
        return {
          id: `cart-${Date.now()}`, // 고유 ID 생성
          restaurantId,
          restaurantName: restaurantInfo.restaurantName,
          restaurantNameEn: restaurantInfo.restaurantNameEn,
          restaurantNameJa: restaurantInfo.restaurantNameJa,
          restaurantNameZhCn: restaurantInfo.restaurantNameZhCn,
          restaurantNameZhTw: restaurantInfo.restaurantNameZhTw,
          restaurantNameKo: restaurantInfo.restaurantNameKo,
          items: [item],
        };
      }
      
      // 다른 레스토랑의 메뉴를 추가하려는 경우는 컨텍스트에서 처리하므로 여기서는 무시
      if (state.restaurantId !== restaurantId) {
        return state;
      }
      
      // 이미 카트에 있는 아이템인지 확인
      const existingItemIndex = state.items.findIndex(
        existingItem => existingItem.id === item.id
      );
      
      if (existingItemIndex >= 0) {
        // 이미 있는 아이템이면 수량만 업데이트
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + item.quantity,
        };
        
        return {
          ...state,
          items: updatedItems,
        };
      }
      
      // 새 아이템 추가
      return {
        ...state,
        items: [...state.items, item],
      };
    }
    
    case 'REPLACE_CART': {
      const { restaurantId, restaurantInfo, item } = action.payload;
      
      // 새 카트로 완전히 교체, 다국어 레스토랑 이름 지원 추가
      return {
        id: `cart-${Date.now()}`, // 고유 ID 생성
        restaurantId,
        restaurantName: restaurantInfo.restaurantName,
        restaurantNameEn: restaurantInfo.restaurantNameEn,
        restaurantNameJa: restaurantInfo.restaurantNameJa,
        restaurantNameZhCn: restaurantInfo.restaurantNameZhCn,
        restaurantNameZhTw: restaurantInfo.restaurantNameZhTw,
        restaurantNameKo: restaurantInfo.restaurantNameKo,
        items: [item],
      };
    }
    
    case 'UPDATE_ITEM_QUANTITY': {
      if (!state) return null;
      
      const { itemId, quantity } = action.payload;
      
      if (quantity <= 0) {
        // 수량이 0 이하면 아이템 제거
        const updatedItems = state.items.filter(item => item.id !== itemId);
        
        // 카트에 아이템이 없으면 null 반환
        if (updatedItems.length === 0) {
          return null;
        }
        
        return {
          ...state,
          items: updatedItems,
        };
      }
      
      // 수량 업데이트
      const updatedItems = state.items.map(item => 
        item.id === itemId
          ? { ...item, quantity }
          : item
      );
      
      return {
        ...state,
        items: updatedItems,
      };
    }
    
    case 'REMOVE_ITEM': {
      if (!state) return null;
      
      const { itemId } = action.payload;
      
      const updatedItems = state.items.filter(item => item.id !== itemId);
      
      // 카트에 아이템이 없으면 null 반환
      if (updatedItems.length === 0) {
        return null;
      }
      
      return {
        ...state,
        items: updatedItems,
      };
    }
    
    case 'CLEAR_CART':
      return null;
      
    case 'UPDATE_PRICES': {
      // 언어 변경 시 가격을 업데이트하는 액션
      if (!state || !state.items.length) return state;
      
      // 언어 변경이 있어도 state 자체는 변경 없음
      // cart 항목이 각 통화별 가격 정보를 가지고 있으므로
      // 렌더링 시 getTotalPrice에서 현재 언어에 맞는 가격 계산
      return { ...state }; // 얕은 복사를 통해 참조 변경하여 리렌더링 트리거
    }
    
    case 'SET_ORDER_ID': {
      if (!state) return null;
      
      return {
        ...state,
        orderId: action.payload.orderId
      };
    }
      
    default:
      return state;
  }
};

// 카트 컨텍스트 생성
const CartContext = createContext<CartContextType | undefined>(undefined);

// 카트 프로바이더 컴포넌트
export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, null, loadCartFromStorage);
  const [switchMessage, setSwitchMessage] = useState<string | null>(null);
  const [currentRestaurantId, setCurrentRestaurantId] = useState<string | null>(null);
  const [showCartReplaceConfirm, setShowCartReplaceConfirm] = useState<boolean>(false);
  const [pendingCartItem, setPendingCartItem] = useState<{ restaurantId: string; restaurantInfo: RestaurantInfo; item: CartItem } | null>(null);
  const [canceledItemId, setCanceledItemId] = useState<string | null>(null);
  const [restaurantMinOrderAmount, _setRestaurantMinOrderAmount] = useState<MinOrderAmounts | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(getCurrentLanguage());
  
  // 최소 주문 금액 설정 함수를 래핑하여 호출 추적
  const setRestaurantMinOrderAmount = useCallback((amounts: MinOrderAmounts | null) => {
    _setRestaurantMinOrderAmount(amounts);
  }, []);
  
  // 언어 변경 시 장바구니 가격 업데이트 함수를 먼저 정의
  const updateCartPricesForLanguage = useCallback((language: SupportedLanguage) => {
    // 현재 언어가 이미 같은 경우 불필요한 업데이트 방지
    if (language === currentLanguage) {
      return;
    }
    
    // 언어에 맞는 가격으로 카트 가격 업데이트 액션 디스패치
    dispatch({
      type: 'UPDATE_PRICES',
      payload: { language }
    });
    
    // 강제 리렌더링을 위해 현재 언어 업데이트
    setCurrentLanguage(language);
  }, [currentLanguage, dispatch]);
  
  // 장바구니와 메뉴 아이템 로직 연동을 위한 현재 보고 있는 식당 ID
  useEffect(() => {
    // 카트가 있으면 현재 보고 있는 식당 ID 기록
    if (cart) {
      setCurrentRestaurantId(String(cart.restaurantId)); // 문자열로 변환하여 전달
    } else {
      setCurrentRestaurantId(null);
    }
  }, [cart]);
  
  // 카트 상태가 변경될 때마다 로컬 스토리지에 저장
  useEffect(() => {
    if (cart) {
      localStorage.setItem('cart', JSON.stringify(cart));
      localStorage.setItem('cartVersion', CART_DATA_VERSION.toString());
    } else {
      localStorage.removeItem('cart');
      // 카트가 비어있을 때 관련 상태 초기화
      setCurrentRestaurantId(null);
      setRestaurantMinOrderAmount(null);
    }
  }, [cart, setRestaurantMinOrderAmount]); // setRestaurantMinOrderAmount 추가
  
  // 언어 변경 감지 및 가격 업데이트
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.language) {
        const newLanguage = customEvent.detail.language as SupportedLanguage;
        setCurrentLanguage(newLanguage);
        
        // 장바구니 가격 업데이트 (언어에 맞게)
        updateCartPricesForLanguage(newLanguage);
      }
    };
    
    // 언어 변경 이벤트 리스너 등록
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, [updateCartPricesForLanguage]); // updateCartPricesForLanguage를 의존성 배열에 추가
  
  // 카트에 아이템 추가
  const addToCart = (restaurantId: string, restaurantInfo: RestaurantInfo, item: CartItem) => {
    // restaurantInfo의 유효성 검사
    if (!restaurantInfo || typeof restaurantInfo.restaurantName !== 'string') {
      console.error('Invalid restaurantInfo provided:', restaurantInfo);
      // 기본값 설정
      restaurantInfo = {
        restaurantName: 'Unknown Restaurant',
        restaurantNameEn: 'Unknown Restaurant'
      };
    } else {
      // 각 언어별 이름에 대한 개별 검사
      restaurantInfo = {
        ...restaurantInfo,
        restaurantName: restaurantInfo.restaurantName || 'Unknown Restaurant',
        restaurantNameEn: restaurantInfo.restaurantNameEn || restaurantInfo.restaurantName || 'Unknown Restaurant',
        restaurantNameJa: restaurantInfo.restaurantNameJa || restaurantInfo.restaurantNameEn || restaurantInfo.restaurantName,
        restaurantNameZhCn: restaurantInfo.restaurantNameZhCn || restaurantInfo.restaurantNameEn || restaurantInfo.restaurantName,
        restaurantNameZhTw: restaurantInfo.restaurantNameZhTw || restaurantInfo.restaurantNameEn || restaurantInfo.restaurantName,
        restaurantNameKo: restaurantInfo.restaurantNameKo || restaurantInfo.restaurantNameEn || restaurantInfo.restaurantName
      };
    }
    
    // 카트가 명확히 비어있거나, 같은 레스토랑의 아이템인 경우
    if (cart === null || (cart && cart.restaurantId === restaurantId)) {
      dispatch({
        type: 'ADD_ITEM',
        payload: { restaurantId, restaurantInfo, item },
      });
    } else {
      // 다른 레스토랑의 메뉴를 추가하려는 경우 - 확인 팝업 표시
      setPendingCartItem({ restaurantId, restaurantInfo, item });
      setShowCartReplaceConfirm(true);
    }
  };
  
  // 카트 교체 확인
  const confirmReplaceCart = () => {
    if (pendingCartItem) {
      const { restaurantId, restaurantInfo, item } = pendingCartItem;
      dispatch({
        type: 'REPLACE_CART',
        payload: { restaurantId, restaurantInfo, item },
      });
      
      // 교체 확인 후 메시지 설정
      setSwitchMessage(`Switched to ${restaurantInfo.restaurantName}. Your previous cart has been replaced.`);
      
      // 상태 초기화
      setShowCartReplaceConfirm(false);
      setPendingCartItem(null);
    }
  };
  
  // 카트 교체 취소
  const cancelReplaceCart = () => {
    // 취소된 아이템 ID 설정
    if (pendingCartItem) {
      setCanceledItemId(pendingCartItem.item.id);

      // 잠시 후 취소된 아이템 ID 초기화 (컴포넌트가 반응할 시간 제공)
      setTimeout(() => {
        setCanceledItemId(null);
      }, 100);
    }
    
    setShowCartReplaceConfirm(false);
    setPendingCartItem(null);
  };
  
  // 취소된 아이템 초기화
  const clearCanceledItem = () => {
    setCanceledItemId(null);
  };
  
  // 전환 메시지 초기화
  const clearSwitchMessage = () => {
    setSwitchMessage(null);
  };
  
  // 아이템 수량 업데이트
  const updateItemQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      // 수량이 0 이하면 아이템 제거 처리
      removeItem(itemId);
      return;
    }
    
    dispatch({
      type: 'UPDATE_ITEM_QUANTITY',
      payload: { itemId, quantity },
    });
  };
  
  // 아이템 제거
  const removeItem = (itemId: string) => {
    // 마지막 아이템을 제거하는 경우 현재 레스토랑 ID도 초기화하기 위해
    // cart 아이템 수 확인
    if (cart && cart.items.length === 1) {
      setCurrentRestaurantId(null);
    }
    
    dispatch({
      type: 'REMOVE_ITEM',
      payload: { itemId },
    });
  };
  
  // 카트 비우기
  const clearCart = () => {
    setSwitchMessage(null);
    setCurrentRestaurantId(null); // 카트를 비울 때 현재 레스토랑 ID도 초기화
    setRestaurantMinOrderAmount(null); // 카트를 비울 때 최소 주문 금액도 초기화
    dispatch({ type: 'CLEAR_CART' });
  };
  
  // 총 가격 계산
  const getTotalPrice = (): number => {
    if (!cart) return 0;
    
    // 현재 언어를 기반으로 통화 결정
    const currency = getCurrencyFromLanguage(currentLanguage);
    
    return cart.items.reduce((total, item) => {
      let itemPrice: number = 0;
      
      // 현재 통화에 따라 프로모션 가격 또는 일반 가격 사용
      switch (currency) {
        case 'USD':
          if (item.isPromotionUsd && item.promotionPriceUsd) {
            itemPrice = item.promotionPriceUsd;
          } else {
            itemPrice = item.priceUsd ?? item.price;
          }
          break;
        case 'JPY':
          if (item.isPromotionJpy && item.promotionPriceJpy) {
            itemPrice = item.promotionPriceJpy;
          } else {
            itemPrice = item.priceJpy ?? item.price;
          }
          break;
        case 'CNY':
          if (item.isPromotionCny && item.promotionPriceCny) {
            itemPrice = item.promotionPriceCny;
          } else {
            itemPrice = item.priceCny ?? item.price;
          }
          break;
        case 'TWD':
          if (item.isPromotionTwd && item.promotionPriceTwd) {
            itemPrice = item.promotionPriceTwd;
          } else {
            itemPrice = item.priceTwd ?? item.price;
          }
          break;
        case 'KRW':
        default:
          if (item.isPromotionKrw && item.promotionPriceKrw) {
            itemPrice = item.promotionPriceKrw;
          } else {
            itemPrice = item.priceKrw ?? item.price;
          }
          break;
      }
      
      const itemTotal = itemPrice * item.quantity;
      
      // 옵션 가격은 메뉴 가격에 이미 합산되어 있으므로 제거
      // (API에서 옵션이 포함된 가격을 제공한다고 했으므로)
      
      return total + itemTotal;
    }, 0);
  };
  
  // 총 아이템 수량 계산
  const getTotalItems = (): number => {
    if (!cart) return 0;
    
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  };
  
  // 최소 주문 금액 가져오기 (현재 언어에 맞는 통화 사용)
  const getMinOrderAmount = (): number | null => {
    if (restaurantMinOrderAmount === null) {
      return null;
    }
    
    // 현재 언어를 기반으로 통화 결정
    const targetCurrency = getCurrencyFromLanguage(currentLanguage);
    
    // 서버에서 제공된 통화별 최소 주문 금액 직접 사용
    switch (targetCurrency) {
      case 'USD': return restaurantMinOrderAmount.usd;
      case 'JPY': return restaurantMinOrderAmount.jpy;
      case 'CNY': return restaurantMinOrderAmount.cny;
      case 'TWD': return restaurantMinOrderAmount.twd;
      case 'KRW':
      default: return restaurantMinOrderAmount.krw;
    }
  };
  
  // 배달 가능 여부 확인
  const isDeliveryAvailable = (minOrderAmount: number | null): boolean => {
    if (minOrderAmount === null) {
      return true; // 최소 주문 금액이 없으면 배달 가능
    }
    
    const totalPrice = getTotalPrice();
    
    // 최소 주문 금액의 1% 미만 차이면 허용 (더 유연한 비교)
    const isWithinOnePercent = (minOrderAmount - totalPrice) < (minOrderAmount * 0.01);
    const result = totalPrice >= minOrderAmount || isWithinOnePercent;
    
    return result;
  };
  
  // 최소 주문 금액까지 남은 금액 계산
  const getAmountToMinOrder = (minOrderAmount: number | null): number => {
    if (minOrderAmount === null) return 0;
    
    const totalPrice = getTotalPrice();
    
    // 최소 주문 금액의 1% 미만 차이면 이미 충분한 것으로 간주
    const isWithinOnePercent = (minOrderAmount - totalPrice) < (minOrderAmount * 0.01);
    
    // 이미 최소 주문 금액에 도달했거나 매우 근접한 경우
    if (totalPrice >= minOrderAmount || isWithinOnePercent) {
      return 0;
    }
    
    return minOrderAmount - totalPrice;
  };
  
  // 장바구니에서 주문 ID 가져오기
  const fetchOrderId = async (
    addressId: string,
    reservationDateTime?: string,
    originStayId?: string,
  ): Promise<CartResponse | null> => {
    if (!cart) return null;
    
    try {
      // 현재 언어 가져오기
      const language = getCurrentLanguage();
      
      // API 요청 데이터 변환
      const serviceUserId = getCurrentServiceUserId();
      const cartData = transformCartToRequest(
        cart,
        addressId,
        language.toUpperCase(),
        reservationDateTime,
        serviceUserId,
        originStayId,
      );
      
      // 문자열로 변환하여 안전하게 비교
      const restaurantIdStr = String(cart.restaurantId).trim();
      const isGiftShop = restaurantIdStr === 'gift-shop-1';
      
      // 기념품 여부에 따라 다른 API 호출
      const response = isGiftShop 
        ? await createCartGift(cartData)
        : await createCart(cartData);
      
      // 응답에서 orderId 가져오기
      if (response && response.orderId) {
        // 상태 업데이트
        dispatch({ type: 'SET_ORDER_ID', payload: { orderId: response.orderId } });
        
        // 로컬 스토리지 업데이트
        if (cart) {
          const updatedCart = { ...cart, orderId: response.orderId };
          localStorage.setItem('cart', JSON.stringify(updatedCart));
        }
        
        return response;
      }
      
      // orderId가 없으면 직접 조회 시도
      if (response && response.id) {
        const cartResponse = await getCart(response.id, language.toUpperCase());
        if (cartResponse && cartResponse.orderId) {
          // 상태 업데이트
          dispatch({ type: 'SET_ORDER_ID', payload: { orderId: cartResponse.orderId } });
          
          // 로컬 스토리지 업데이트
          if (cart) {
            const updatedCart = { ...cart, orderId: cartResponse.orderId };
            localStorage.setItem('cart', JSON.stringify(updatedCart));
          }
          
          return cartResponse;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching order ID:', error);
      // 에러를 그대로 전파하여 호출부에서 에러 코드(MIN_ORDER_AMOUNT_NOT_MET 등)를
      // 감지해 사용자 친화 메시지로 처리할 수 있도록 함
      throw error;
    }
  };
  
  // 컨텍스트 값
  const contextValue: CartContextType = {
    cart,
    currentRestaurantId,
    setCurrentRestaurantId,
    showCartReplaceConfirm,
    pendingCartItem,
    canceledItemId,
    restaurantMinOrderAmount,
    setRestaurantMinOrderAmount,
    addToCart,
    updateItemQuantity,
    removeItem,
    clearCart,
    getTotalPrice,
    getTotalItems,
    getMinOrderAmount,
    isDeliveryAvailable,
    getAmountToMinOrder,
    switchMessage,
    clearSwitchMessage,
    confirmReplaceCart,
    cancelReplaceCart,
    clearCanceledItem,
    updateCartPricesForLanguage,
    fetchOrderId
  };
  
  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

// 카트 컨텍스트 사용을 위한 훅
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
